import { Fragment, useCallback } from "react";
import { Box } from "@mui/material";
import { FormSection } from "./FormSection";
import { FormField } from "./FormField";
import type { FormFieldConfig } from "./FormField";
import { MultiOptionField } from "./MultiOptionField";
import { CustomButton } from "@/components/common/customButton";

type Section = {
  section: string;
  fields: FormFieldConfig[];
  column: number;
  columns?: number; // subcolumns
};

interface DynamicFormProps {
  config: Section[];
  values: Record<string, any>;
  onChange: (field: string, value: any) => void;
  optionsMap?: Record<string, any[]>;
  numColumns?: number;
  performGeocoding?: (values: Record<string, any>) => void;
  requiredFields?: string[];
}

// Stable default so memoized FormFields aren't re-rendered by a new `{}` each render
const EMPTY_OPTIONS_MAP: NonNullable<DynamicFormProps["optionsMap"]> = {};

export const DynamicForm: React.FC<DynamicFormProps> = ({
  config,
  values,
  onChange,
  optionsMap = EMPTY_OPTIONS_MAP,
  numColumns,
  performGeocoding,
  requiredFields = [],
}) => {
  const maxColumnFromConfig = Math.max(...config.map((s) => s.column ?? 0), 0);
  const columnCount = Math.max(numColumns ?? 1, maxColumnFromConfig + 1);

  // Group sections by column index
  const columns: Section[][] = Array.from({ length: columnCount }, () => []);
  config.forEach((section) => {
    const col = Math.min(section.column ?? 0, columnCount - 1);
    columns[col].push(section);
  });

  const updateGeoCode = useCallback(() => {
    if (performGeocoding) {
      performGeocoding(values);
    }
  }, [performGeocoding, values]);

  return (
    <Box sx={{ display: "flex", gap: 3, width: "100%" }}>
      {columns.map((sectionsInCol, colIdx) => (
        <Box
          key={colIdx}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          {sectionsInCol.map((section) => (
            <div key={section.section}>
              <FormSection
                key={section.section}
                title={section.section}
                columns={section.columns ?? 1}
              >
                {section.fields.map((field) => {
                  let value = values[field.field];

                  if (
                    field.type !== "Borrowers" &&
                    value &&
                    field.optionLabelKey &&
                    value[field.optionLabelKey]
                  ) {
                    value = value[field.optionLabelKey];
                  }

                  if (field.type === "Borrowers") {
                    return (
                      <Fragment key={field.field}>
                        <MultiOptionField
                          field={field.field}
                          label={field.label}
                          value={values[field.field]}
                          onChange={onChange}
                        />
                      </Fragment>
                    );
                  }

                  return (
                    <Fragment key={field.field}>
                      <FormField
                        config={field}
                        value={value}
                        onChange={onChange}
                        optionsMap={optionsMap}
                        required={requiredFields.includes(field.field)}
                      />
                    </Fragment>
                  );
                })}
              </FormSection>
              {section.section === "Asset Details" && (
                <CustomButton
                  text={"Update Map"}
                  handleButtonPress={updateGeoCode}
                  isButtonDisabled={false}
                />
              )}
            </div>
          ))}
        </Box>
      ))}
    </Box>
  );
};
